import { useState, useEffect } from 'react';
import { X, Palette, RotateCcw, Check } from 'lucide-react';
import { useSelector } from 'react-redux';
import api from '../api/config';
import './ThemeCustomizer.css';

const ThemeCustomizer = ({ isOpen, onClose }) => {
  const { user } = useSelector(state => state.auth);
  const [primaryColor, setPrimaryColor] = useState('#c4fffc'); // Celeste pastel por defecto
  const [secondaryColor, setSecondaryColor] = useState('#000000'); // Negro por defecto
  const [tempPrimary, setTempPrimary] = useState('#c4fffc');
  const [tempSecondary, setTempSecondary] = useState('#000000');
  const [showSaved, setShowSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Colores predefinidos sugeridos - Ampliado
  const suggestedPrimary = [
    // Colores originales
    { name: 'Celeste Paper Boat', color: '#c4fffc' },
    { name: 'Azul Cielo', color: '#87CEEB' },
    { name: 'Verde Menta', color: '#98FB98' },
    { name: 'Lavanda', color: '#E6E6FA' },
    { name: 'Rosa Pastel', color: '#FFE4E1' },
    { name: 'Amarillo Suave', color: '#FFFACD' },
    { name: 'Coral', color: '#FFB6C1' },
    { name: 'Turquesa', color: '#AFEEEE' },
    // Nuevos colores pastel
    { name: 'Melocotón', color: '#FFDAB9' },
    { name: 'Lila', color: '#DDA0DD' },
    { name: 'Aqua', color: '#B0E0E6' },
    { name: 'Verde Lima', color: '#BFFF00' },
    { name: 'Naranja Pastel', color: '#FFD4A3' },
    { name: 'Gris Perla', color: '#E8E8E8' },
    { name: 'Beige', color: '#F5F5DC' },
    { name: 'Salmón', color: '#FFA07A' },
    { name: 'Azul Bebé', color: '#B5D7FF' },
    { name: 'Verde Agua', color: '#7FFFD4' },
    { name: 'Rosa Chicle', color: '#FF69B4' },
    { name: 'Oro Pálido', color: '#EEE8AA' }
  ];

  const suggestedSecondary = [
    // Colores originales
    { name: 'Negro Clásico', color: '#000000' },
    { name: 'Azul Oscuro', color: '#000080' },
    { name: 'Verde Oscuro', color: '#006400' },
    { name: 'Morado Oscuro', color: '#4B0082' },
    { name: 'Marrón', color: '#654321' },
    { name: 'Gris Oscuro', color: '#333333' },
    { name: 'Vino', color: '#722F37' },
    { name: 'Azul Marino', color: '#191970' },
    // Nuevos colores oscuros
    { name: 'Carbón', color: '#36454F' },
    { name: 'Índigo', color: '#4B0082' },
    { name: 'Terracota', color: '#8B4513' },
    { name: 'Petróleo', color: '#005F6B' },
    { name: 'Ciruela', color: '#8B008B' },
    { name: 'Verde Bosque', color: '#228B22' },
    { name: 'Rojo Oscuro', color: '#8B0000' },
    { name: 'Gris Acero', color: '#71797E' },
    { name: 'Azul Cobalto', color: '#0047AB' },
    { name: 'Chocolate', color: '#7B3F00' },
    { name: 'Púrpura Real', color: '#663399' },
    { name: 'Verde Oliva', color: '#556B2F' }
  ];

  useEffect(() => {
    // Cargar colores guardados del localStorage
    const savedPrimary = localStorage.getItem('theme-primary-color') || '#c4fffc';
    const savedSecondary = localStorage.getItem('theme-secondary-color') || '#000000';

    setPrimaryColor(savedPrimary);
    setTempPrimary(savedPrimary);
    setSecondaryColor(savedSecondary);
    setTempSecondary(savedSecondary);

    applyColor('primary', savedPrimary);
    applyColor('secondary', savedSecondary);
  }, []);

  // Aplicar cambios automáticamente cuando se selecciona un color
  useEffect(() => {
    if (isOpen) {
      applyColor('primary', tempPrimary);
      applyColor('secondary', tempSecondary);
    }
  }, [tempPrimary, tempSecondary, isOpen]);

  // Detectar cambios
  useEffect(() => {
    const currentPrimary = localStorage.getItem('theme-primary-color') || '#c4fffc';
    const currentSecondary = localStorage.getItem('theme-secondary-color') || '#000000';

    setHasChanges(
      tempPrimary !== currentPrimary ||
      tempSecondary !== currentSecondary
    );
  }, [tempPrimary, tempSecondary]);

  const applyColor = (type, color) => {
    const root = document.documentElement;

    // Limpiar estilos inline de nav items activos para que CSS tome control
    document.querySelectorAll('.nav-item.active').forEach(el => {
      el.style.removeProperty('background');
      el.style.removeProperty('color');
    });

    // Disparar evento personalizado para actualizar componentes
    window.dispatchEvent(new Event('theme-color-change'));

    if (type === 'primary') {
      // Aplicar color primario (reemplaza celeste)
      root.style.setProperty('--theme-primary', color);
      root.style.setProperty('--theme-primary-light', adjustColor(color, 20));
      root.style.setProperty('--theme-primary-dark', adjustColor(color, -20));

      // Convertir hex a RGB para usar en rgba() para hovers
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      root.style.setProperty('--theme-primary-rgb', `${r}, ${g}, ${b}`);

      // Generar color más oscuro para gráficos
      const darkerColor = adjustColor(color, -30);
      root.style.setProperty('--theme-primary-darker', darkerColor);

      // NO aplicar al header - mantener blanco
      // El header se mantiene blanco original

      // Textos del sidebar (solo los no activos)
      document.querySelectorAll('.sidebar-nav .nav-item:not(.active)').forEach(el => {
        el.style.color = color;
      });

      // Título y subtítulo del sidebar
      document.querySelectorAll('.sidebar-header h2, .sidebar-header p').forEach(el => {
        el.style.color = color;
      });

      // NO aplicar estilos inline a elementos activos - dejar que CSS lo maneje

      // Aplicar hover a nav items del sidebar usando CSS variable
      document.querySelectorAll('.sidebar-nav .nav-item').forEach(el => {
        el.style.setProperty('--hover-bg', `${color}26`);
      });

      // Botones del sidebar
      document.querySelectorAll('.sidebar-toggle, .logout-btn').forEach(el => {
        el.style.background = `${color}20`;
        el.style.borderColor = color;
      });

      // Super Admin destacado
      document.querySelectorAll('.nav-item.super-admin').forEach(el => {
        el.style.background = `linear-gradient(45deg, ${color}1A, ${color}33)`;
        el.style.borderColor = `${color}4D`;
      });

      // Aplicar color sólido a layout-content
      document.querySelectorAll('.layout-content').forEach(el => {
        el.style.background = color;
      });

      // Aplicar color sólido a dashboard-content
      document.querySelectorAll('.dashboard-content').forEach(el => {
        el.style.background = color;
      });

      // Aplicar color sólido a todas las páginas con sus contenedores específicos
      document.querySelectorAll('.cotizaciones-container, .clientes-container, .equipo-container, .ordenes-venta-container, .ordenes-compra-container, .proveedores-container, .finanzas-container, .calendario-container, .manual-container, .perfil-container, .configuracion-container, .urgencias-container, .transito-container, .empresa-container, .super-admin-container, .configuracion-empresa-container').forEach(el => {
        el.style.background = color;
      });

      // Botones primarios
      document.querySelectorAll('.btn-primary, .btn-nuevo-cliente, .btn-nueva-cotizacion').forEach(el => {
        el.style.background = color;
      });

      // Badge de empresa activa
      document.querySelectorAll('.empresa-activa-badge').forEach(el => {
        el.style.background = color;
        el.style.borderColor = '#000';
      });

      // Logos de empresa en selector
      document.querySelectorAll('.empresa-logo').forEach(el => {
        el.style.background = color;
      });

      // Opciones activas en dropdown de empresa
      document.querySelectorAll('.empresa-option.active').forEach(el => {
        el.style.background = color;
        el.style.color = '#000';
      });

      // Cards headers
      document.querySelectorAll('.card-header, .stats-card, .stat-card').forEach(el => {
        const currentBg = el.style.background;
        if (currentBg && currentBg.includes('#c4fffc')) {
          el.style.background = color;
        }
      });
    } else {
      // Aplicar color secundario (reemplaza negro)
      root.style.setProperty('--theme-secondary', color);
      root.style.setProperty('--theme-secondary-light', adjustColor(color, 30));

      // Sidebar
      document.querySelectorAll('.sidebar').forEach(el => {
        el.style.background = color;
      });

      // Bordes del sidebar
      document.querySelectorAll('.sidebar-header').forEach(el => {
        el.style.borderBottomColor = adjustColor(color, 20);
      });

      // Bordes generales
      document.querySelectorAll('.layout-header').forEach(el => {
        el.style.borderBottomColor = color;
      });

      // Todos los bordes negros
      document.querySelectorAll('*').forEach(el => {
        const style = window.getComputedStyle(el);
        if (style.borderColor === 'rgb(0, 0, 0)') {
          el.style.borderColor = color;
        }
      });
    }
  };

  const adjustColor = (color, percent) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;

    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16).slice(1);
  };

  const handleSave = async () => {
    try {
      setPrimaryColor(tempPrimary);
      setSecondaryColor(tempSecondary);

      // Guardar en localStorage
      localStorage.setItem('theme-primary-color', tempPrimary);
      localStorage.setItem('theme-secondary-color', tempSecondary);

      // Aplicar colores
      applyColor('primary', tempPrimary);
      applyColor('secondary', tempSecondary);

      // Guardar en la base de datos
      if (user && user.id) {
        await api.put(`/users/${user.id}/theme-preferences`, {
          primaryColor: tempPrimary,
          secondaryColor: tempSecondary
        });
      }

      // Mostrar mensaje de guardado
      setShowSaved(true);
      setTimeout(() => {
        setShowSaved(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error al guardar preferencias de tema:', error);
      // Aún así mostramos confirmación porque se guardó en localStorage
      setShowSaved(true);
      setTimeout(() => {
        setShowSaved(false);
        onClose();
      }, 1500);
    }
  };

  const handleReset = () => {
    const defaultPrimary = '#c4fffc';
    const defaultSecondary = '#000000';

    setTempPrimary(defaultPrimary);
    setTempSecondary(defaultSecondary);
    setPrimaryColor(defaultPrimary);
    setSecondaryColor(defaultSecondary);

    // Limpiar localStorage
    localStorage.removeItem('theme-primary-color');
    localStorage.removeItem('theme-secondary-color');

    // Aplicar colores por defecto
    applyColor('primary', defaultPrimary);
    applyColor('secondary', defaultSecondary);
  };

  if (!isOpen) return null;

  return (
    <div className="theme-customizer-overlay" onClick={onClose}>
      <div className="theme-customizer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="theme-header" style={{ backgroundColor: tempPrimary }}>
          <div className="theme-title">
            <Palette size={24} />
            <h2>Personalizar Colores</h2>
          </div>
          <button className="theme-close" onClick={onClose}>
            <X size={24} color="#000" />
          </button>
        </div>

        {showSaved ? (
          <div className="theme-saved-message">
            <Check size={64} />
            <h3>Colores Guardados</h3>
            <p>Tu tema personalizado ha sido aplicado</p>
          </div>
        ) : (
          <>
            <div className="theme-content">
              <div className="color-section">
                <h3>Color Primario</h3>
                <p className="color-description">
                  Reemplaza el celeste en fondos, detalles y elementos destacados
                </p>

                <div className="color-picker-row">
                  <input
                    type="color"
                    value={tempPrimary}
                    onChange={(e) => setTempPrimary(e.target.value)}
                    className="color-input"
                    id="primary-color-picker"
                  />
                  <input
                    type="text"
                    value={tempPrimary.toUpperCase()}
                    onChange={(e) => setTempPrimary(e.target.value)}
                    className="color-text"
                    placeholder="#C4FFFC"
                  />
                  <button
                    type="button"
                    className="btn-open-picker"
                    onClick={() => document.getElementById('primary-color-picker').click()}
                  >
                    <Palette size={18} color="#000" />
                    Elegir Cualquier Color
                  </button>
                </div>

                <div className="suggested-colors">
                  {suggestedPrimary.map((item) => (
                    <button
                      key={item.color}
                      className={`color-chip ${tempPrimary === item.color ? 'active' : ''}`}
                      style={{ background: item.color }}
                      onClick={() => setTempPrimary(item.color)}
                      title={item.name}
                    />
                  ))}
                </div>
              </div>

              <div className="color-section">
                <h3>Color Secundario</h3>
                <p className="color-description">
                  Reemplaza el negro en sidebar, bordes y textos principales
                </p>

                <div className="color-picker-row">
                  <input
                    type="color"
                    value={tempSecondary}
                    onChange={(e) => setTempSecondary(e.target.value)}
                    className="color-input"
                    id="secondary-color-picker"
                  />
                  <input
                    type="text"
                    value={tempSecondary.toUpperCase()}
                    onChange={(e) => setTempSecondary(e.target.value)}
                    className="color-text"
                    placeholder="#000000"
                  />
                  <button
                    type="button"
                    className="btn-open-picker"
                    onClick={() => document.getElementById('secondary-color-picker').click()}
                  >
                    <Palette size={18} color="#000" />
                    Elegir Cualquier Color
                  </button>
                </div>

                <div className="suggested-colors">
                  {suggestedSecondary.map((item) => (
                    <button
                      key={item.color}
                      className={`color-chip ${tempSecondary === item.color ? 'active' : ''}`}
                      style={{ background: item.color }}
                      onClick={() => setTempSecondary(item.color)}
                      title={item.name}
                    />
                  ))}
                </div>
              </div>

              <div className="theme-preview">
                <h3>Vista Previa</h3>
                <div className="preview-container">
                  <div className="preview-sidebar" style={{ background: tempSecondary }}>
                    <div className="preview-menu-item" style={{ color: tempPrimary }}>
                      Menú Item
                    </div>
                    <div className="preview-menu-item" style={{
                      background: tempPrimary,
                      color: tempSecondary,
                      padding: '8px 12px',
                      borderRadius: '4px'
                    }}>
                      Item Activo
                    </div>
                  </div>
                  <div className="preview-main" style={{ background: tempPrimary }}>
                    <div className="preview-header" style={{
                      background: 'white',
                      borderBottom: `2px solid ${tempSecondary}`
                    }}>
                      <span style={{ color: tempSecondary }}>Header (siempre blanco)</span>
                    </div>
                    <div className="preview-content" style={{ background: tempPrimary }}>
                      <div className="preview-card" style={{
                        background: 'white',
                        border: `2px solid ${tempSecondary}`,
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}>
                        <span style={{ color: tempSecondary }}>Card</span>
                      </div>
                      <div className="preview-button" style={{
                        background: tempSecondary,
                        color: tempPrimary,
                        border: `2px solid ${tempPrimary}`
                      }}>
                        Botón
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Espaciador invisible para permitir scroll completo en móvil */}
              <div style={{ height: window.innerWidth <= 768 ? '250px' : '0px', width: '100%' }}></div>
            </div>

            <div className="theme-footer" style={{
              position: window.innerWidth <= 768 ? 'fixed' : 'relative',
              bottom: window.innerWidth <= 768 ? 0 : 'auto',
              left: window.innerWidth <= 768 ? 0 : 'auto',
              right: window.innerWidth <= 768 ? 0 : 'auto',
              width: window.innerWidth <= 768 ? '100vw' : 'auto',
              zIndex: window.innerWidth <= 768 ? 999999 : 'auto',
              display: 'flex',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              gap: window.innerWidth <= 768 ? '12px' : 'auto',
              boxShadow: window.innerWidth <= 768 ? '0 -4px 12px rgba(0, 0, 0, 0.15)' : 'none'
            }}>
              <button className="btn-reset" onClick={handleReset} style={{
                width: window.innerWidth <= 768 ? '100%' : 'auto',
                padding: window.innerWidth <= 768 ? '14px 20px' : '10px 20px',
                fontSize: window.innerWidth <= 768 ? '16px' : '14px'
              }}>
                <RotateCcw size={18} color="#000" />
                Restaurar Colores
              </button>
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={!hasChanges}
                style={{
                  width: window.innerWidth <= 768 ? '100%' : 'auto',
                  padding: window.innerWidth <= 768 ? '14px 24px' : '10px 24px',
                  fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                  backgroundColor: hasChanges ? tempPrimary : '#ccc',
                  color: '#000',
                  border: `2px solid #000`,
                  opacity: hasChanges ? 1 : 0.5,
                  cursor: hasChanges ? 'pointer' : 'not-allowed'
                }}
              >
                Guardar Cambios
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ThemeCustomizer;